import React from "react";

/**
 * VAPE.ESSENCE — Custom 3D vector logo.
 *
 * Composition:
 *  - Isometric "vape cloud" mark: two offset glyphs (back layer = lime, front = orange)
 *    stacked to create 3D depth without external assets.
 *  - Radial glow filter for that neon-lit feel.
 *  - Wordmark using tight display font, split by an accent dot.
 *  - Group animates on hover (rotate + jump) via CSS.
 */
export default function Logo({
  size = 34,
  showWord = true,
  className = "",
  wordClassName = "",
  testId = "brand-logo",
}) {
  const id = React.useId();
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-2.5 group ${className}`}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        aria-label="Vape Essence"
        className="drop-shadow-[0_0_18px_rgba(255,90,31,0.55)] transition-transform duration-500 group-hover:rotate-[8deg] group-hover:scale-[1.06]"
      >
        <defs>
          {/* Orange gradient — primary face */}
          <linearGradient id={`${id}-orange`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFB347" />
            <stop offset="45%" stopColor="#FF5A1F" />
            <stop offset="100%" stopColor="#B72E00" />
          </linearGradient>
          {/* Lime gradient — depth layer */}
          <linearGradient id={`${id}-lime`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#B6FFEC" />
            <stop offset="55%" stopColor="#00FFC2" />
            <stop offset="100%" stopColor="#00A67F" />
          </linearGradient>
          {/* Highlight shimmer */}
          <linearGradient id={`${id}-shine`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0" />
            <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          {/* Soft outer glow */}
          <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* --- BACK LAYER (lime, offset for 3D depth) --- */}
        <g transform="translate(6,7)" opacity="0.85">
          <path
            d="M32 4 L40 26 L52 26 L36 60 L28 34 L14 34 Z"
            fill={`url(#${id}-lime)`}
            filter={`url(#${id}-glow)`}
          />
        </g>

        {/* --- MIDDLE SHADOW LAYER (dark, softens the join) --- */}
        <g transform="translate(3,3.5)" opacity="0.55">
          <path
            d="M32 4 L40 26 L52 26 L36 60 L28 34 L14 34 Z"
            fill="#050505"
          />
        </g>

        {/* --- FRONT LAYER (orange, main face) --- */}
        <g>
          <path
            d="M32 4 L40 26 L52 26 L36 60 L28 34 L14 34 Z"
            fill={`url(#${id}-orange)`}
            filter={`url(#${id}-glow)`}
            stroke="#0a0a0a"
            strokeWidth="1"
          />
          {/* Diagonal shine sweep */}
          <path
            d="M32 4 L40 26 L52 26 L36 60 L28 34 L14 34 Z"
            fill={`url(#${id}-shine)`}
            className="mix-blend-overlay"
            opacity="0.65"
          />
          {/* Small inner highlight dot */}
          <circle cx="34" cy="16" r="2.2" fill="#FFFFFF" opacity="0.9" />
        </g>

        {/* --- BOTTOM PULSE RING --- */}
        <circle
          cx="32"
          cy="60"
          r="3"
          fill="none"
          stroke="#00FFC2"
          strokeWidth="1"
          opacity="0.7"
        >
          <animate
            attributeName="r"
            values="3;9;3"
            dur="2.4s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.7;0;0.7"
            dur="2.4s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>

      {showWord && (
        <span
          className={`font-display font-black tracking-tighter leading-none ${wordClassName || "text-base sm:text-lg"}`}
        >
          <span className="text-white">VAPE</span>
          <span className="mx-[2px] text-[#00FFC2] drop-shadow-[0_0_6px_rgba(0,255,194,0.65)]">
            .
          </span>
          <span className="bg-clip-text text-transparent bg-gradient-to-br from-[#FFB347] via-[#FF5A1F] to-[#FF3366]">
            ESSENCE
          </span>
        </span>
      )}
    </span>
  );
}
